import { Component, OnInit, signal, computed } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Temporal } from '@js-temporal/polyfill';
import { SommelierService } from '@proxy/sommeliers';
import { finalize } from 'rxjs';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { APP_ICONS } from '../shared/icons/app-icons';

interface ClientData {
  userId: string;
  clientName: string;
  clientSurname: string;
  totalPurchases: number;
  lastPurchaseDate: string | null;
  phoneNumber: string;
  whatsAppHref: string | null;
}

@Component({
  selector: 'app-sommelier-clients',
  templateUrl: './sommelier-clients.component.html',
  standalone: false
})
export class SommelierClientsComponent implements OnInit {
  dateRange = {
    start: null as Temporal.PlainDate | null,
    end: null as Temporal.PlainDate | null,
  };

  clients = signal<ClientData[]>([]);
  searchTerm = signal('');
  isLoading = false;

  filteredClients = computed(() => {
    const term = this.searchTerm().toLowerCase().trim();
    const allClients = this.clients();
    if (!term) return allClients;
    return allClients.filter(c =>
      c.clientName.toLowerCase().includes(term) ||
      c.clientSurname.toLowerCase().includes(term) ||
      `${c.clientName} ${c.clientSurname}`.toLowerCase().includes(term)
    );
  });
  safeWhatsAppIcon: SafeHtml;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private sommelierService: SommelierService,
    private sanitizer: DomSanitizer
  ) {
    this.safeWhatsAppIcon = this.sanitizer.bypassSecurityTrustHtml(APP_ICONS.whatsapp);
  }

  ngOnInit(): void {
    // El filtro de fecha debe venir vacío al entrar, independientemente de los parámetros
    this.dateRange = {
      start: null,
      end: null,
    };

    this.loadClients();
  }

  private parseDateFromQuery(dateString: string): Temporal.PlainDate | null {
    try {
      const parts = dateString.split('/');
      if (parts.length === 3) {
        const day = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10);
        const year = parseInt(parts[2], 10);
        return Temporal.PlainDate.from({ year, month, day });
      }
      if (dateString.includes('-')) {
        return Temporal.PlainDate.from(dateString);
      }
    } catch {
      return null;
    }
    return null;
  }

  onDateRangeChange(range: { start: Temporal.PlainDate; end: Temporal.PlainDate }): void {
    this.dateRange = range;
    this.updateQueryParams();
    this.loadClients();
  }

  private updateQueryParams(): void {
    const queryParams: any = { ...this.route.snapshot.queryParams };

    if (this.dateRange.start) {
      const start = this.dateRange.start;
      queryParams['start'] = `${String(start.day).padStart(2, '0')}/${String(start.month).padStart(2, '0')}/${start.year}`;
    } else {
      delete queryParams['start'];
    }

    if (this.dateRange.end) {
      const end = this.dateRange.end;
      queryParams['end'] = `${String(end.day).padStart(2, '0')}/${String(end.month).padStart(2, '0')}/${end.year}`;
    } else {
      delete queryParams['end'];
    }

    // Eliminar period si cambiamos las fechas manualmente
    delete queryParams['period'];

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: queryParams,
      replaceUrl: true
    });
  }

  loadClients(): void {
    this.isLoading = true;

    let from: string | undefined = undefined;
    let to: string | undefined = undefined;

    if (this.dateRange.start) {
      const fromDate = new Date(
        this.dateRange.start.year,
        this.dateRange.start.month - 1,
        this.dateRange.start.day
      );
      from = fromDate.toISOString();
    }

    if (this.dateRange.end) {
      const toDate = new Date(
        this.dateRange.end.year,
        this.dateRange.end.month - 1,
        this.dateRange.end.day,
        23, 59, 59, 999
      );
      to = toDate.toISOString();
    }

    this.sommelierService.getMyClients({
      from,
      to
    } as any)
      .pipe(
        finalize(() => this.isLoading = false)
      )
      .subscribe({
        next: (result) => {
          const mappedClients = result.map(client => {
            const phoneNumber = client.phoneNumber || '';
            return {
              userId: client.userId,
              clientName: client.clientName || '',
              clientSurname: client.clientSurname || '',
              totalPurchases: client.totalPurchases || 0,
              lastPurchaseDate: client.lastPurchaseDate || null,
              phoneNumber,
              whatsAppHref: this.buildWhatsAppLink(phoneNumber)
            };
          });
          this.clients.set(mappedClients);
        },
        error: (error) => {
          console.error('Error al cargar clientes:', error);
        }
      });
  }

  goToClientDetail(userId: string): void {
    this.router.navigate([userId], { relativeTo: this.route });
  }

  formatDate(dateString: string | null): string {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch {
      return '-';
    }
  }

  formatCurrency(amount: number): string {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      minimumFractionDigits: 2
    }).format(amount);
  }

  private buildWhatsAppLink(raw?: string | null): string | null {
    if (!raw) return null;

    let p = raw.startsWith('+')
      ? raw.slice(1)                          // E.164 guardado: solo quitar '+'
      : raw.replace(/\D+/g, '');             // fallback: solo dígitos

    if (p.startsWith('00')) p = p.slice(2);

    return `https://wa.me/${p}`;
  }
}


